import React, { useState, useEffect, useRef, useMemo } from 'react';

import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';

import Icon from './Icon';
import { C, FONT, SHADOW } from './tokens';

const norm = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const CommandPalette = ({ open, onClose, modules }) => {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return modules.slice(0, 8);
    return modules
      .filter((m) => norm(m.name).includes(norm(q)) || norm(m.group).includes(norm(q)))
      .slice(0, 9);
  }, [query, modules]);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // Autofocus after the dialog transition.
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 40);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const go = (item) => {
    if (!item) return;
    onClose();
    window.location.assign(item.url);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[cursor]);
    }
  };

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.children[cursor];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const noResults = query.trim() && results.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          mt: '70px', width: 620, maxWidth: '92%', borderRadius: '12px', boxShadow: SHADOW.palette,
          overflow: 'hidden', display: 'flex', flexDirection: 'column', m: 0, alignSelf: 'flex-start',
        },
      }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
    >
      {/* Input */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '11px', p: '15px 18px',
        borderBottom: `1px solid ${C.borderSoft}`,
      }}>
        <Icon name="search" size={19} color={C.textFaint} />
        <Box
          component="input"
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
          onKeyDown={onKeyDown}
          placeholder="Buscar módulo…"
          sx={{
            flex: 1, border: 0, outline: 0, background: 'transparent',
            font: `400 15px/1.2 ${FONT}`, color: C.text,
          }}
        />
        <Box component="button" onClick={onClose} sx={{
          font: `700 10px/1 ${FONT}`, color: C.textMuted, border: `1px solid ${C.border}`,
          borderRadius: '4px', p: '4px 6px', cursor: 'pointer', background: 'none',
        }}>ESC</Box>
      </Box>

      {/* Results */}
      <Box ref={listRef} sx={{
        maxHeight: 340, overflowY: 'auto', p: '9px 8px', display: 'flex', flexDirection: 'column', gap: '2px',
      }}>
        {noResults && (
          <Box sx={{
            p: '26px 12px', textAlign: 'center', font: `400 12.5px/1.5 ${FONT}`, color: C.textMuted,
          }}>
            Ningún módulo coincide con «{query}».
          </Box>
        )}
        {results.map((item, i) => {
          const selected = i === cursor;
          return (
            <Box
              key={`${item.group}-${item.name}`}
              onClick={() => go(item)}
              onMouseEnter={() => setCursor(i)}
              sx={{
                display: 'flex', alignItems: 'center', gap: '11px', p: '10px 12px', borderRadius: '8px',
                cursor: 'pointer', bgcolor: selected ? C.turquoiseTint : 'transparent',
              }}
            >
              <Icon name={item.icon} size={16} color={selected ? C.turquoiseText : C.textFaint} />
              <Box component="span" sx={{ font: `${selected ? 700 : 400} 13px/1 ${FONT}`, flex: 1 }}>
                {item.name}
              </Box>
              <Box component="span" sx={{ font: `400 11px/1 ${FONT}`, color: C.textMuted }}>
                {item.group}
              </Box>
              {selected && <Icon name="corner-down-left" size={15} color={C.turquoiseText} />}
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Box sx={{
        borderTop: `1px solid ${C.borderSoft}`, p: '10px 16px', display: 'flex', gap: '16px',
        font: `400 10.5px/1 ${FONT}`, color: C.textMuted,
      }}>
        <span>↑↓ navegar</span>
        <span>↵ abrir</span>
        <Box component="span" sx={{ ml: 'auto' }}>Solo módulos permitidos por tus policies</Box>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
